import { c as _c } from "react/compiler-runtime";
import { ButtonProps, FormInput, Hint } from '@systemeio/ui';
import EmailTemplateSelectorNewEditorModal from 'modules/email/components/email-template-selector/email-template-selector-modal';
import { MailingId } from 'modules/mailing/types/mailing-interface';
import { useEffect, useState } from 'react';
import DOMPurify from 'isomorphic-dompurify';
import ButtonLikeLink from 'shared/components/button-like-link';
import Confirmation from 'shared/components/confirmation-modal';
import { SubstituteInterface } from 'shared/components/dashboard-mail-editor/components/substitutes-block';
import { MailingPoolStatusColors, MailingPoolStatusHelpMessages } from 'shared/components/dashboard-mail-editor/enums/mailing-pool-status-enum';
import { useEmailOptions } from 'shared/components/dashboard-mail-editor/hooks/use-email-options';
import { getEmailStatusLink } from 'shared/components/dashboard-mail-editor/utils/get-email-status-link';
import { isWithRecipient } from 'shared/components/dashboard-mail-editor/utils/is-with-recipients';
import LinkWithoutPrefetch from 'shared/components/link-without-prefetch';
import TransLoco from 'shared/components/trans-loco';
import { useLocoTranslation } from 'shared/hooks/use-loco-translation';
import useUser from 'shared/hooks/use-user';
import { twMerge } from 'tailwind-merge';
import { DashboardMailEditorCommonProps, ExtendedState } from '../../types/common';
import { NewEditor } from './new-editor';
import { MagicWandIcon } from '@systemeio/ui/icons';
import { EditorExtensionEnum, emailTextExtensions, TipTapEmailEditor } from '@systemeio/tip-tap';
import { useSubstitutes } from '../../hooks/use-substitutes';
import { useEmailFooterData } from '../../hooks/use-email-footer-data';
export type EditorBlockProps<T extends ExtendedState> = Pick<DashboardMailEditorCommonProps<T>, 'isPreFetching' | 'inModal' | 'errors' | 'tempState' | 'onChangeEmail' | 'disabled'> & {
  withRecipient?: boolean;
} & {
  isSwitchEditorButtonShown?: boolean;
  switchEditorButtonProps?: ButtonProps;
  isEditorEditable?: boolean;
  showSettingsLink?: boolean;
  mailingId?: MailingId;
  additionalSubstitutes?: SubstituteInterface[];
  className?: string;
};
const EditorBlock = t0 => {
  const $ = _c(105);
  const {
    disabled,
    errors,
    inModal,
    isEditorEditable: t1,
    isPreFetching,
    isSwitchEditorButtonShown: t2,
    onChangeEmail,
    showSettingsLink: t3,
    tempState,
    withRecipient,
    mailingId,
    additionalSubstitutes,
    className
  } = t0;
  const isEditorEditable = t1 === undefined ? true : t1;
  const isSwitchEditorButtonShown = t2 === undefined ? false : t2;
  const showSettingsLink = t3 === undefined ? true : t3;
  const {
    t
  } = useLocoTranslation();
  const {
    user
  } = useUser();
  const {
    data: options
  } = useEmailOptions();
  const [isTemplateModalOpened, setIsTemplateModalOpened] = useState(false);
  const [isConfirmationModalOpened, setIsConfirmationModalOpened] = useState(false);
  let t4;
  if ($[0] !== onChangeEmail || $[1] !== tempState) {
    t4 = async templateId => {
      tempState && (await onChangeEmail({
        ...tempState,
        useNewEditor: true,
        template: templateId
      }));
      setIsTemplateModalOpened(false);
    };
    $[0] = onChangeEmail;
    $[1] = tempState;
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  const onChooseTemplateModal = t4;
  let t5;
  if ($[3] !== onChangeEmail || $[4] !== tempState) {
    t5 = async () => {
      tempState && (await onChangeEmail({
        ...tempState,
        useNewEditor: !tempState.useNewEditor
      }));
      setIsConfirmationModalOpened(false);
    };
    $[3] = onChangeEmail;
    $[4] = tempState;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const onConfirmationModalOpened = t5;
  const {
    substitutionData
  } = useSubstitutes(additionalSubstitutes);
  const [emailFooterDataContent, setEmailFooterDataContent] = useState();
  const {
    emailFooterData
  } = useEmailFooterData();
  let t6;
  if ($[6] !== emailFooterData || $[7] !== tempState) {
    t6 = () => {
      if (tempState?.fromEmail) {
        const domain = tempState.fromEmail.split("@")[1];
        if (emailFooterData) {
          setEmailFooterDataContent(emailFooterData.items.find(item => item.domainName === domain)?.content);
        }
      }
    };
    $[6] = emailFooterData;
    $[7] = tempState;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  const t7 = tempState?.fromEmail;
  let t8;
  if ($[9] !== emailFooterData || $[10] !== t7) {
    t8 = [t7, emailFooterData];
    $[9] = emailFooterData;
    $[10] = t7;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  useEffect(t6, t8);
  const t9 = !inModal && "p-5";
  const t10 = tempState?.useNewEditor && "h-[800px] lg:h-fit";
  let t11;
  if ($[12] !== className || $[13] !== t10 || $[14] !== t9) {
    t11 = twMerge("bg-background-secondary flex w-full flex-col gap-5 rounded-md", t9, t10, className);
    $[12] = className;
    $[13] = t10;
    $[14] = t9;
    $[15] = t11;
  } else {
    t11 = $[15];
  }
  let t12;
  if ($[16] !== disabled || $[17] !== errors.recipient || $[18] !== isPreFetching || $[19] !== onChangeEmail || $[20] !== t || $[21] !== tempState || $[22] !== withRecipient) {
    t12 = withRecipient && isWithRecipient(withRecipient, tempState) && <FormInput disabled={disabled} isPreFetching={isPreFetching} label={t("dashboard.email.recipient_address")} required={true} value={tempState?.recipient || ""} error={errors.recipient} onChange={e => tempState && onChangeEmail({
      ...(tempState as T),
      recipient: e.target.value
    })} />;
    $[16] = disabled;
    $[17] = errors.recipient;
    $[18] = isPreFetching;
    $[19] = onChangeEmail;
    $[20] = t;
    $[21] = tempState;
    $[22] = withRecipient;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  let t13;
  if ($[24] !== options || $[25] !== t || $[26] !== user) {
    t13 = user && options && <Hint color={MailingPoolStatusColors[options.emailPool]} label={<TransLoco t={t} i18nKey={MailingPoolStatusHelpMessages[options.emailPool]} components={{
      a: <a href={getEmailStatusLink(user?.dashboardLocale)} target="_blank" rel="noreferrer" className="primary-link" />
    }} />} popoverClassName="w-[300px] sm:w-[500px]">{t("dashboard.email.subject")}</Hint>;
    $[24] = options;
    $[25] = t;
    $[26] = user;
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  const t14 = tempState?.subject || "";
  let t15;
  if ($[28] !== onChangeEmail || $[29] !== tempState) {
    t15 = e_0 => tempState && onChangeEmail({
      ...tempState,
      subject: e_0.target.value
    });
    $[28] = onChangeEmail;
    $[29] = tempState;
    $[30] = t15;
  } else {
    t15 = $[30];
  }
  let t16;
  if ($[31] !== disabled || $[32] !== errors.subject || $[33] !== isPreFetching || $[34] !== t13 || $[35] !== t14 || $[36] !== t15) {
    t16 = <FormInput disabled={disabled} isPreFetching={isPreFetching} label={t13} required={true} value={t14} onChange={t15} error={errors.subject} />;
    $[31] = disabled;
    $[32] = errors.subject;
    $[33] = isPreFetching;
    $[34] = t13;
    $[35] = t14;
    $[36] = t15;
    $[37] = t16;
  } else {
    t16 = $[37];
  }
  let t17;
  if ($[38] !== options || $[39] !== t || $[40] !== user) {
    t17 = user && options && <Hint label={t("dashboard.email.preview_text_tooltip")}>{t("dashboard.email.preview_text_label")}</Hint>;
    $[38] = options;
    $[39] = t;
    $[40] = user;
    $[41] = t17;
  } else {
    t17 = $[41];
  }
  const t18 = tempState?.previewText || "";
  let t19;
  if ($[42] !== onChangeEmail || $[43] !== tempState) {
    t19 = e_1 => tempState && onChangeEmail({
      ...tempState,
      previewText: e_1.target.value
    });
    $[42] = onChangeEmail;
    $[43] = tempState;
    $[44] = t19;
  } else {
    t19 = $[44];
  }
  let t20;
  if ($[45] !== disabled || $[46] !== errors.previewText || $[47] !== isPreFetching || $[48] !== t17 || $[49] !== t18 || $[50] !== t19) {
    t20 = <FormInput disabled={disabled} isPreFetching={isPreFetching} label={t17} value={t18} error={errors.previewText} onChange={t19} />;
    $[45] = disabled;
    $[46] = errors.previewText;
    $[47] = isPreFetching;
    $[48] = t17;
    $[49] = t18;
    $[50] = t19;
    $[51] = t20;
  } else {
    t20 = $[51];
  }
  let t21;
  if ($[52] !== t) {
    t21 = t("dashboard.email.body");
    $[52] = t;
    $[53] = t21;
  } else {
    t21 = $[53];
  }
  let t22;
  if ($[54] !== t21) {
    t22 = <span>{t21}</span>;
    $[54] = t21;
    $[55] = t22;
  } else {
    t22 = $[55];
  }
  let t23;
  if ($[56] !== disabled || $[57] !== isPreFetching || $[58] !== isSwitchEditorButtonShown || $[59] !== t || $[60] !== tempState) {
    t23 = isSwitchEditorButtonShown && !isPreFetching && <><ButtonLikeLink disabled={disabled} onClick={() => {
        if (tempState) {
          if (!tempState.useNewEditor && !tempState.html) {
            !tempState.useNewEditor && setIsTemplateModalOpened(true);
          } else {
            setIsConfirmationModalOpened(true);
          }
        }
      }} className="gap-1"><MagicWandIcon className="h-[14]px w-[14px]" />{tempState?.useNewEditor ? t("dashboard.email.switch_to_the_classic_editor") : t("dashboard.email.switch_to_the_visual_editor")}</ButtonLikeLink></>;
    $[56] = disabled;
    $[57] = isPreFetching;
    $[58] = isSwitchEditorButtonShown;
    $[59] = t;
    $[60] = tempState;
    $[61] = t23;
  } else {
    t23 = $[61];
  }
  let t24;
  if ($[62] !== t22 || $[63] !== t23) {
    t24 = <div className="flex justify-between">{t22}{t23}</div>;
    $[62] = t22;
    $[63] = t23;
    $[64] = t24;
  } else {
    t24 = $[64];
  }
  let t25;
  if ($[65] !== disabled || $[66] !== emailFooterDataContent || $[67] !== errors.common || $[68] !== isEditorEditable || $[69] !== isPreFetching || $[70] !== mailingId || $[71] !== onChangeEmail || $[72] !== substitutionData || $[73] !== tempState) {
    t25 = <div className="relative h-full">{tempState?.useNewEditor && mailingId ? <NewEditor tempState={tempState} isPreFetching={isPreFetching} mailingId={mailingId} disabled={disabled || !isEditorEditable} /> : <div><TipTapEmailEditor extensions={emailTextExtensions} isPreFetching={isPreFetching} isEditable={!disabled && isEditorEditable} content={tempState?.body || undefined} onChange={data => tempState && onChangeEmail({
          ...tempState,
          body: data
        })} extensionOptions={{
          [EditorExtensionEnum.Substitution]: {
            substitutions: substitutionData
          }
        }} className={twMerge(emailFooterDataContent && "rounded-b-none", errors.common.length !== 0 && "border-danger")} />{emailFooterDataContent && <div className="border-text-secondary/30 rounded-b-lg border border-t-0 p-2"><div dangerouslySetInnerHTML={{
            __html: DOMPurify.sanitize(emailFooterDataContent.replace(/\n/g, "<br />"))
          }} /></div>}</div>}</div>;
    $[65] = disabled;
    $[66] = emailFooterDataContent;
    $[67] = errors.common;
    $[68] = isEditorEditable;
    $[69] = isPreFetching;
    $[70] = mailingId;
    $[71] = onChangeEmail;
    $[72] = substitutionData;
    $[73] = tempState;
    $[74] = t25;
  } else {
    t25 = $[74];
  }
  let t26;
  if ($[75] !== t24 || $[76] !== t25) {
    t26 = <div className="flex basis-full flex-col gap-1 text-sm font-medium">{t24}{t25}</div>;
    $[75] = t24;
    $[76] = t25;
    $[77] = t26;
  } else {
    t26 = $[77];
  }
  let t27;
  if ($[78] !== showSettingsLink || $[79] !== t) {
    t27 = showSettingsLink && <span className="mt-2"><TransLoco t={t} i18nKey="dashboard.email.change_footer_link" components={{
        a: <LinkWithoutPrefetch href="/profile/mailing-settings" className="primary-link" />
      }} /></span>;
    $[78] = showSettingsLink;
    $[79] = t;
    $[80] = t27;
  } else {
    t27 = $[80];
  }
  let t28;
  if ($[81] !== errors.body) {
    t28 = errors.body && <p className="text-danger text-sm">{errors.body}</p>;
    $[81] = errors.body;
    $[82] = t28;
  } else {
    t28 = $[82];
  }
  let t29;
  if ($[83] === Symbol.for("react.memo_cache_sentinel")) {
    t29 = () => setIsConfirmationModalOpened(false);
    $[83] = t29;
  } else {
    t29 = $[83];
  }
  let t30;
  if ($[84] !== t || $[85] !== tempState?.useNewEditor) {
    t30 = tempState?.useNewEditor ? t("dashboard.email.switch_to_the_classic_editor_message") : t("dashboard.email.switch_to_the_visual_editor_message");
    $[84] = t;
    $[85] = tempState?.useNewEditor;
    $[86] = t30;
  } else {
    t30 = $[86];
  }
  let t31;
  if ($[87] !== isConfirmationModalOpened || $[88] !== onConfirmationModalOpened || $[89] !== t30) {
    t31 = <Confirmation opened={isConfirmationModalOpened} onClose={t29} onConfirm={onConfirmationModalOpened} confirmationText={t30} />;
    $[87] = isConfirmationModalOpened;
    $[88] = onConfirmationModalOpened;
    $[89] = t30;
    $[90] = t31;
  } else {
    t31 = $[90];
  }
  let t32;
  if ($[91] === Symbol.for("react.memo_cache_sentinel")) {
    t32 = () => setIsTemplateModalOpened(false);
    $[91] = t32;
  } else {
    t32 = $[91];
  }
  let t33;
  if ($[92] !== isTemplateModalOpened || $[93] !== onChooseTemplateModal) {
    t33 = <EmailTemplateSelectorNewEditorModal opened={isTemplateModalOpened} onClose={t32} onChoose={onChooseTemplateModal} />;
    $[92] = isTemplateModalOpened;
    $[93] = onChooseTemplateModal;
    $[94] = t33;
  } else {
    t33 = $[94];
  }
  let t34;
  if ($[95] !== t11 || $[96] !== t12 || $[97] !== t16 || $[98] !== t20 || $[99] !== t26 || $[100] !== t27 || $[101] !== t28 || $[102] !== t31 || $[103] !== t33) {
    t34 = <div className={t11}>{t12}{t16}{t20}{t26}{t27}{t28}{t31}{t33}</div>;
    $[95] = t11;
    $[96] = t12;
    $[97] = t16;
    $[98] = t20;
    $[99] = t26;
    $[100] = t27;
    $[101] = t28;
    $[102] = t31;
    $[103] = t33;
    $[104] = t34;
  } else {
    t34 = $[104];
  }
  return t34;
};
export default EditorBlock;